import React, { useEffect, useRef, useState } from 'react';

import trapInactiveDefault from '../../assets/trap_inactive.png';
import trapActiveDefault from '../../assets/trap_active.png';
import loreCornerDetail from '../../assets/lore/lore_details.png';

function CornerDetail({ className = '', rotationClass = '' }) {
  return (
    <img
      src={loreCornerDetail}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none absolute h-[52px] w-[52px] select-none opacity-80 ${rotationClass} ${className}`}
    />
  );
}

/**
 * TrapQTEOverlay
 * - Shows a shrinking ring timer. Tap before it closes to succeed.
 * - If the timer hits 0, shows the "active/triggered" trap image briefly, shows a red damage flash,
 *   then calls onFail.
 *
 * IMPORTANT:
 * - onSuccess/onFail/onAutoDisarm are stored in refs so the timer effect does NOT restart every render.
 * - No eslint disables needed.
 */
export default function TrapQTEOverlay({
  open,
  durationMs = 1200,
  label = 'DISARM',
  onSuccess,
  onFail,
  onAutoDisarm, // optional
  onActivateVisual, // optional: fires exactly when trap switches to triggered image
  inactiveImageSrc,
  activeImageSrc,
  failRevealMs = 450, // how long to show the triggered trap after timeout (image stays)
}) {
  const [progress, setProgress] = useState(1); // 1 -> 0
  const [phase, setPhase] = useState('countdown'); // "countdown" | "triggered"
  const [flashKey, setFlashKey] = useState(0); // used to restart flash animation
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const firedRef = useRef(false);

  // When the trap times out, a short delay shows the triggered graphic before firing onFail.
  // Store this timeout so it can be cleared if the overlay unmounts early.
  const failTimeoutRef = useRef(null);

  // Store callbacks in refs to avoid restarting the timer effect due to new function identities.
  const onSuccessRef = useRef(onSuccess);
  const onFailRef = useRef(onFail);
  const onAutoDisarmRef = useRef(onAutoDisarm);
  const onActivateVisualRef = useRef(onActivateVisual);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    onFailRef.current = onFail;
  }, [onFail]);
  useEffect(() => {
    onAutoDisarmRef.current = onAutoDisarm;
  }, [onAutoDisarm]);
  useEffect(() => {
    onActivateVisualRef.current = onActivateVisual;
  }, [onActivateVisual]);

  useEffect(() => {
    if (!open) return;

    firedRef.current = false;
    setPhase('countdown');
    setProgress(1);

    startRef.current = performance.now();

    const tick = (t) => {
      const elapsed = t - startRef.current;
      const p = Math.max(0, 1 - elapsed / durationMs);
      setProgress(p);

      if (p <= 0) {
        if (!firedRef.current) {
          firedRef.current = true;

          // Fire SFX exactly when we switch to the triggered/active trap visual
          onActivateVisualRef.current?.();

          setPhase('triggered');
          setProgress(0);

          // restart the flash
          setFlashKey((k) => k + 1);

          // Briefly show the "triggered" trap image, and run the flash, then fail.
          // Save the timeout so it can be cleared on unmount to prevent running
          // after the overlay is closed.
          failTimeoutRef.current = window.setTimeout(() => {
            onFailRef.current?.();
            // Reset the ref after firing to avoid clearing an old timeout later.
            failTimeoutRef.current = null;
          }, Math.max(0, failRevealMs));
        }
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;

      // Clear any pending fail timeout to avoid invoking onFail after unmount.
      if (failTimeoutRef.current) {
        clearTimeout(failTimeoutRef.current);
        failTimeoutRef.current = null;
      }
    };
  }, [open, durationMs, failRevealMs]);

  const handleTap = () => {
    if (!open) return;
    if (phase !== 'countdown') return;
    if (firedRef.current) return;

    firedRef.current = true;
    onSuccessRef.current?.();
  };

  const handleAuto = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open) return;
    if (phase !== 'countdown') return;
    if (firedRef.current) return;

    firedRef.current = true;
    onAutoDisarmRef.current?.();
  };

  if (!open) return null;

  const deg = Math.round(progress * 360);
  const showActive = phase === 'triggered';
  const centerSrc = showActive
    ? activeImageSrc || trapActiveDefault
    : inactiveImageSrc || trapInactiveDefault;

  // flash animation lasts a bit longer than the reveal so it visibly fades
  const flashMs = Math.max(520, failRevealMs + 220);

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.78)', touchAction: 'none' }}
      onPointerDown={(e) => {
        // Block clicks from reaching the map, but DON'T disarm.
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Local keyframes so we don't depend on global CSS */}
      <style>{`
        @keyframes ddTrapDamageFlash {
          0%   { opacity: 0; }
          10%  { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>

      {/* Damage flash/vignette on trigger: shows, then fades away */}
      {showActive && (
        <div
          key={flashKey}
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-[1000]"
          style={{
            boxShadow:
              'inset 0 0 0 9999px rgba(140, 0, 0, 0.05), inset 0 0 90px 28px rgba(255, 50, 50, 0.32), inset 0 0 190px 80px rgba(255, 0, 0, 0.22)',
            filter: 'blur(0.6px)',
            animation: `ddTrapDamageFlash ${flashMs}ms ease-out forwards`,
          }}
        />
      )}

      <div
        className="relative z-[1001] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col items-center bg-black/80 px-10 pb-8 pt-6 text-white shadow-[0_20px_60px_rgba(0,0,0,0.72)] sm:w-[392px]"
        style={{ fontFamily: 'Karma, serif' }}
      >
        <CornerDetail className="left-5 top-4" rotationClass="rotate-180" />
        <CornerDetail className="right-5 top-4" rotationClass="-rotate-90" />
        <CornerDetail className="left-5 bottom-4" rotationClass="rotate-90" />
        <CornerDetail className="right-5 bottom-4" />

        <div className="relative z-[1] flex w-full flex-col items-center gap-4 px-2 pt-4">
          <div
            className="relative flex items-center justify-center select-none"
            style={{
              width: 240,
              height: 240,
              borderRadius: '9999px',
              cursor: 'pointer',
              background: `conic-gradient(rgba(255,255,255,0.95) ${deg}deg, rgba(255,255,255,0.12) 0deg)`,
              padding: 10,
              boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
            }}
            onPointerDown={(e) => {
              // Tap must be ON the circle to disarm
              e.preventDefault();
              e.stopPropagation();
              handleTap();
            }}
          >
            <div
              className="relative flex items-center justify-center text-center font-semibold tracking-widest"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '9999px',
                background: 'rgba(0,0,0,0.90)',
                border: '2px solid rgba(255,255,255,0.22)',
                color: 'rgba(255,255,255,0.95)',
                padding: 18,
                overflow: 'hidden',
              }}
            >
              {/* Center trap illustration */}
              <img
                src={centerSrc}
                alt="Trap"
                draggable={false}
                style={{
                  width: 120,
                  height: 120,
                  objectFit: 'contain',
                  filter: showActive
                    ? 'drop-shadow(0 0 14px rgba(255,80,80,0.45))'
                    : 'drop-shadow(0 0 10px rgba(0,0,0,0.35))',
                  transform: showActive ? 'scale(1.02)' : 'scale(1.0)',
                  transition: 'transform 120ms ease, filter 120ms ease',
                  opacity: showActive ? 1 : 0.95,
                }}
              />

              {/* Label */}
              <div
                className="px-5 text-center leading-tight"
                style={{
                  position: 'absolute',
                  bottom: 16,
                  left: 0,
                  right: 0,
                  fontSize: 13,
                  letterSpacing: 2,
                  color: 'rgba(255,255,255,0.9)',
                }}
              >
                {showActive ? (
                  <>
                    TRIGGERED
                  </>
                ) : (
                  <>
                    TAP TO
                    <br />
                    {label}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="max-w-[220px] text-center text-[16px] leading-tight text-white/75">
            {showActive ? (
              <>
                Too late.
              </>
            ) : (
              <>
                Tap before the
                <br />
                circle closes.
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
